import {
  AfterViewInit,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Modal, ModalConfig, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { LoginProviders, User } from '@app/shared/models/user.model';
import { EventManager } from '@app/shared/utils/event-manager';
import { AppsFlyersService } from '@shared/services/appsFlyers.service';
import { LoggerService } from '@shared/services/logger.service';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { SharedModule } from '@app/shared/shared.module';
import { AnalyticsService } from '@app/shared/services/analytics.service';
import { UserRepository } from '@app/shared/state/user/user.repository';
import { GsapService } from '@app/shared/animations/gsap.service';
import { OneSignalService, TagName } from '@app/shared/services/one-signal.service';
import { FirehoseAnalyticsService } from '@app/core/analytics/firehose/public-api';
import { JulietBuddyComponent } from '@app/shared/components/juliet-buddy/juliet-buddy.component';
import { WELCOME_MODAL_POSE } from '@app/shared/components/juliet-buddy/juliet-buddy.config';

export enum AnimationStep {
  Start = 0,
  ShowModalContent = 1,
  End = 2,
}

@Component({
  selector: 'slotcom-welcome-modal',
  templateUrl: './welcome-modal.component.html',
  styleUrls: ['./welcome-modal.component.scss'],
  standalone: true,
  imports: [ButtonComponent, SharedModule, JulietBuddyComponent],
})
export class WelcomeModalComponent implements OnInit, AfterViewInit {
  readonly welcomeBonusModalData = inject<ModalConfig>(MAT_DIALOG_DATA);

  readonly modalReward = this.welcomeBonusModalData.reward;
  readonly welcomeModalPose = WELCOME_MODAL_POSE;
  currentStep = signal<AnimationStep>(AnimationStep.Start);
  user = signal<User>(null);

  private timerId?: ReturnType<typeof setTimeout>;

  private readonly STEP_DURATIONS: Record<AnimationStep, number> = {
    [AnimationStep.Start]: 0,
    [AnimationStep.ShowModalContent]: 500,
    [AnimationStep.End]: 0,
  };

  @ViewChild('bgContainer') bgContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('title') title!: ElementRef<HTMLDivElement>;
  @ViewChild('juliet') juliet!: ElementRef<HTMLDivElement>;
  @ViewChild('secondButton') secondButton!: ElementRef<HTMLDivElement>;

  private readonly gsapService = inject(GsapService);
  private readonly eventManager = inject(EventManager);
  private readonly oneSignalService = inject(OneSignalService);
  private readonly userRepo = inject(UserRepository);
  private readonly appsFlyersService = inject(AppsFlyersService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly logger = inject(LoggerService);
  private readonly firehoseAnalyticsService = inject(FirehoseAnalyticsService);

  private readonly _ = effect(() => {
    const step = this.currentStep();
    this.handleStep(step);
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => this.clearTimer());
  }

  ngOnInit(): void {
    this.user.set(this.userRepo.getUser());
    const method = this.resolveRegistrationMethod(this.user());

    this.analyticsService.logEventAndForget('sign_up', { method });
    this.appsFlyersService
      .logEvent('af_complete_registration', { af_registration_method: method })
      .catch((e) => this.logger.error(e));
    this.firehoseAnalyticsService.trackNow('af_complete_registration');
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      this.resetStage();
      this.currentStep.set(AnimationStep.ShowModalContent);
    });
  }

  handleClick(): void {
    const current = this.currentStep();
    if (current >= AnimationStep.ShowModalContent) {
      this.closeModal();
      this.openWelcomeRewardModal();
    }
  }

  openWelcomeRewardModal(): void {
    this.eventManager.openModal(
      new Modal(NonPersistedModalName.WelcomeRewardModalComponent, ModalType.View, this.modalReward)
    );
  }

  closeModal(): void {
    this.oneSignalService.sendTag(TagName.OnboardingSteps, 2).catch((e) => this.logger.error(e));
    this.eventManager.closeModal(
      new Modal(this.welcomeBonusModalData.id, ModalType.Persisted, this.welcomeBonusModalData, '', true)
    );
  }

  openRewardModal(): void {
    this.eventManager.openModal(
      new Modal(this.welcomeBonusModalData.id, ModalType.Persisted, this.welcomeBonusModalData)
    );
  }

  private resolveRegistrationMethod(user: User): LoginProviders {
    if (user.hasFacebook) return 'Facebook';
    if (user.hasAppleId) return 'AppleId';
    if (user.emailConfirmed) return 'Email';
    return 'DeviceId';
  }

  private resetStage(): void {
    this.setElements([this.bgContainer, this.juliet, this.title, this.secondButton], { opacity: 0 });
  }

  private setElements(refs: (ElementRef<HTMLElement> | undefined)[], props: Record<string, unknown>): void {
    for (const ref of refs) {
      if (ref?.nativeElement) {
        this.gsapService.set(ref.nativeElement, props);
      }
    }
  }

  private handleStep(step: AnimationStep): void {
    this.clearTimer();

    switch (step) {
      case AnimationStep.Start:
      case AnimationStep.End:
        break;

      case AnimationStep.ShowModalContent:
        this.gsapService.fromTo(
          this.bgContainer.nativeElement,
          { opacity: 0, scale: 0 },
          { opacity: 1, scale: 1 },
          { duration: 0.5 }
        );
        this.gsapService.fromTo(
          this.secondButton.nativeElement,
          { opacity: 0, scale: 0 },
          { opacity: 1, scale: 1 },
          { duration: 0.5 }
        );
        this.gsapService.slideIn(this.juliet.nativeElement, 'bottom', 1000, { duration: 0.7 });

        this.scheduleNext(step);

        break;
    }
  }

  private scheduleNext(step: AnimationStep): void {
    const duration = this.STEP_DURATIONS[step];
    if (duration > 0) {
      this.timerId = setTimeout(() => {
        if (this.currentStep() < AnimationStep.End) {
          this.currentStep.set(this.currentStep() + 1);
        }
      }, duration);
    }
  }

  private clearTimer(): void {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = undefined;
    }
  }
}
